import { Button, Icon } from "@legal-500/legal500-design-system";
import { useRef, useState } from "react";
import { buildFilesUrl, filesService, type ImageRef } from "@/api/profiles/files";
import styles from "./ImageUploadField.module.css";

export type ImageUploadFieldProps = {
  value: ImageRef | null;
  onChange: (ref: ImageRef | null) => void;
  label?: string;
  slug?: string;
  accept?: string;
  disabled?: boolean;
  uploadLabel?: string;
  changeLabel?: string;
  removeLabel?: string;
  uploadEndpoint?: string;
  onUploadingChange?: (uploading: boolean) => void;
};

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg)$/i;

export function ImageUploadField({
  value,
  onChange,
  label,
  slug,
  accept = "image/png,image/jpeg,image/gif,image/webp",
  disabled = false,
  uploadLabel,
  changeLabel,
  removeLabel = "Remove image",
  uploadEndpoint,
  onUploadingChange,
}: ImageUploadFieldProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const previewUrl = buildFilesUrl(value, slug);
  const isImage = value ? IMAGE_EXT.test(value.filename) : false;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    onUploadingChange?.(true);
    setError(null);
    try {
      const { hash, filename } = uploadEndpoint
        ? await filesService.uploadTo(uploadEndpoint, file)
        : await filesService.upload(file);
      onChange({ hash, filename });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className={styles.root}>
      {label && <span className={styles.label}>{label}</span>}

      {value ? (
        <div className={styles.selected}>
          {isImage && previewUrl ? (
            <img src={previewUrl} alt={value.filename} className={styles.thumb} />
          ) : (
            <div className={styles.fileThumb} aria-hidden="true">
              <Icon icon="external" size="default" />
            </div>
          )}
          <div className={styles.selectedMeta}>
            <p className={styles.selectedTitle}>{value.filename}</p>
            <p className={styles.selectedSubtitle}>Uploaded</p>
          </div>
          <div className={styles.selectedActions}>
            <Button
              type="button"
              variant="outlined"
              size="small"
              aria-label={uploading ? undefined : changeLabel}
              onClick={() => inputRef.current?.click()}
              disabled={disabled || uploading}
            >
              {uploading ? "Uploading…" : "Change"}
            </Button>
            <Button
              type="button"
              variant="icon"
              size="small"
              aria-label={removeLabel}
              onClick={() => onChange(null)}
              disabled={disabled || uploading}
            >
              <Icon icon="cross" />
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className={styles.trigger}
          aria-label={uploading ? undefined : uploadLabel}
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading}
        >
          <Icon icon="plus" size="small" />
          <span>{uploading ? "Uploading…" : "Upload image…"}</span>
        </button>
      )}

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        aria-hidden="true"
        onChange={handleFileChange}
      />
    </div>
  );
}
